import { useEffect, useState } from "react";
import { X, Play } from "lucide-react";
import { Lines, FadeUp } from "./Reveal";
import { LazyVideo } from "./LazyVideo";
import { VIDEOS, POSTERS } from "../i18n";

const workVideos = [
  { src: VIDEOS.openHouse, poster: POSTERS.openHouse },
  { src: VIDEOS.boutique, poster: POSTERS.boutique },
  { src: VIDEOS.conference, poster: POSTERS.conference },
  { src: VIDEOS.property, poster: POSTERS.property },
];

export const SelectedWork = ({ t }) => {
  const [active, setActive] = useState(null);

  const open = (i) => {
    document.querySelectorAll("video").forEach((v) => v.pause());
    setActive(i);
  };
  const close = () => {
    setActive(null);
    document.querySelectorAll("video[data-src]").forEach((v) => {
      if (v.getAttribute("src")) v.play().catch(() => {});
    });
  };

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => e.key === "Escape" && close();
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [active]);

  return (
    <section id="work" className="py-28 md:py-44 bg-sand border-t border-ink/10" data-testid="work-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-24">
          <div>
            <Lines lines={[t.work.title]} className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-ink" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 lg:gap-x-20">
          {t.work.items.map((item, i) => (
            <FadeUp key={i} delay={0.1} className={`mb-20 md:mb-32 ${i % 2 === 1 ? "md:mt-40" : ""}`}>
              <div
                role="button"
                tabIndex={0}
                aria-label={`${item.title} — ${t.work.watch}`}
                onClick={() => open(i)}
                onKeyDown={(e) => e.key === "Enter" && open(i)}
                className="group relative aspect-[4/5] overflow-hidden bg-stone cursor-pointer"
                data-testid={`work-item-${i}`}
              >
                <LazyVideo
                  src={workVideos[i].src}
                  poster={workVideos[i].poster}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-ink/0 opacity-0 group-hover:opacity-100 group-hover:bg-ink/25 transition-all duration-700">
                  <span className="flex items-center gap-3 bg-cream/90 text-ink px-6 py-3.5 font-sans text-[10px] uppercase tracking-[0.25em]">
                    <Play size={12} strokeWidth={2} /> {t.work.watch}
                  </span>
                </div>
              </div>
              <div className="flex items-baseline justify-between gap-4 mt-6 md:mt-7 border-t border-ink/10 pt-4 md:pt-5">
                <h3 className="font-serif text-xl md:text-2xl uppercase tracking-[0.08em] text-ink">{item.title}</h3>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>

      {active !== null && (
        <div
          className="fixed inset-0 z-[70] bg-ink/95 flex items-center justify-center p-4 md:p-10"
          data-testid="video-modal"
          onClick={close}
        >
          <button
            onClick={close}
            aria-label={t.work.close}
            data-testid="video-modal-close"
            className="absolute top-4 right-4 md:top-8 md:right-8 w-12 h-12 flex items-center justify-center text-cream/80 hover:text-cream border border-cream/20 hover:border-cream/50 transition-colors duration-500 z-10"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
          <div className="flex flex-col items-center gap-4 max-h-full" onClick={(e) => e.stopPropagation()}>
            <video
              key={active}
              src={workVideos[active].src}
              poster={workVideos[active].poster}
              autoPlay
              controls
              playsInline
              className="max-h-[80vh] w-auto max-w-full bg-ink"
              data-testid="video-modal-player"
            />
            <p className="font-serif text-cream text-lg md:text-xl">{t.work.items[active].title}</p>
          </div>
        </div>
      )}
    </section>
  );
};

